/**
 * Service for product updates ("what's new" feed).
 * Published updates are visible to all authed users; admins manage all rows.
 * Per-user read state lives in `update_reads`. Client-side writes rely on RLS.
 */

import { supabase } from '@/lib/supabase';
import { ProductUpdate } from '@/types';

const STORAGE_BUCKET = 'images';

interface UpdateRow {
  id: string;
  title: string;
  body_md: string;
  cover_image_url: string | null;
  youtube_url: string | null;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapRow(row: UpdateRow): ProductUpdate {
  return {
    id: row.id,
    title: row.title,
    bodyMd: row.body_md,
    coverImageUrl: row.cover_image_url,
    youtubeUrl: row.youtube_url,
    published: row.published,
    publishedAt: row.published_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ── User-facing ─────────────────────────────────────────────────────────────

export async function fetchPublishedUpdates(): Promise<ProductUpdate[]> {
  const { data, error } = await supabase
    .from('product_updates')
    .select('*')
    .eq('published', true)
    .order('published_at', { ascending: false });
  if (error) { console.error('[Updates] fetchPublishedUpdates', error); throw error; }
  return (data ?? []).map(mapRow);
}

export async function fetchReadUpdateIds(): Promise<string[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('update_reads')
    .select('update_id')
    .eq('user_id', user.id);
  if (error) { console.error('[Updates] fetchReadUpdateIds', error); throw error; }
  return (data ?? []).map((r: { update_id: string }) => r.update_id);
}

export async function markUpdateRead(updateId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { error } = await supabase
    .from('update_reads')
    // ignoreDuplicates (ON CONFLICT DO NOTHING) is load-bearing: update_reads has no UPDATE RLS policy, a real upsert would fail.
    .upsert(
      { user_id: user.id, update_id: updateId },
      { onConflict: 'user_id,update_id', ignoreDuplicates: true },
    );
  if (error) console.error('[Updates] markUpdateRead', error); // non-fatal
}

// ── Admin ───────────────────────────────────────────────────────────────────

export async function fetchAllUpdatesAdmin(): Promise<ProductUpdate[]> {
  const { data, error } = await supabase
    .from('product_updates')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) { console.error('[Updates] fetchAllUpdatesAdmin', error); throw error; }
  return (data ?? []).map(mapRow);
}

async function uploadCover(updateId: string, file: File): Promise<string> {
  const ext = file.type === 'image/gif' ? 'gif'
    : file.type === 'image/png' ? 'png'
    : file.type === 'image/webp' ? 'webp'
    : 'jpg';
  const path = `updates/${updateId}/cover.${ext}`;
  const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
    contentType: file.type,
    upsert: true,
  });
  if (error) throw new Error(`Cover-Upload fehlgeschlagen: ${error.message}`);
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  // Cache-bust: path is deterministic + upsert, so a same-extension replace would
  // otherwise yield an identical URL and browsers/CDN would serve the stale image.
  return `${data.publicUrl}?v=${Date.now()}`;
}

export interface CreateUpdateInput {
  title: string;
  bodyMd: string;
  youtubeUrl: string | null;
  coverFile: File | null;
  publish: boolean;
}

export async function createUpdate(input: CreateUpdateInput): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const id = crypto.randomUUID();
  const coverImageUrl = input.coverFile ? await uploadCover(id, input.coverFile) : null;
  const { error } = await supabase.from('product_updates').insert({
    id,
    title: input.title.trim(),
    body_md: input.bodyMd,
    youtube_url: input.youtubeUrl?.trim() || null,
    cover_image_url: coverImageUrl,
    published: input.publish,
    published_at: input.publish ? new Date().toISOString() : null,
  });
  if (error) { console.error('[Updates] createUpdate', error); throw error; }
}

export interface UpdatePatch {
  title: string;
  bodyMd: string;
  youtubeUrl: string | null;
  coverFile: File | null;      // new file to replace cover; null keeps existing
  published: boolean;
  publishedAt: string | null;  // preserve original first-publish time
}

export async function updateUpdate(id: string, patch: UpdatePatch): Promise<void> {
  const coverImageUrl = patch.coverFile ? await uploadCover(id, patch.coverFile) : undefined;
  const publishedAt = patch.published ? (patch.publishedAt ?? new Date().toISOString()) : null;
  const row: Record<string, unknown> = {
    title: patch.title.trim(),
    body_md: patch.bodyMd,
    youtube_url: patch.youtubeUrl?.trim() || null,
    published: patch.published,
    published_at: publishedAt,
  };
  if (coverImageUrl !== undefined) row.cover_image_url = coverImageUrl;
  const { error } = await supabase.from('product_updates').update(row).eq('id', id);
  if (error) { console.error('[Updates] updateUpdate', error); throw error; }
}

export async function deleteUpdate(id: string): Promise<void> {
  const { error } = await supabase.from('product_updates').delete().eq('id', id);
  if (error) { console.error('[Updates] deleteUpdate', error); throw error; }
  // Remove cover files (non-fatal; remove() ignores missing paths)
  await supabase.storage.from(STORAGE_BUCKET).remove([
    `updates/${id}/cover.jpg`,
    `updates/${id}/cover.png`,
    `updates/${id}/cover.gif`,
    `updates/${id}/cover.webp`,
  ]);
}
